import { ReactElement, useCallback, useState } from 'react'
import { useFormContext, useWatch } from 'react-hook-form'
import { getPolygonNormal } from '@scene'
import numeral from 'numeral'
import { Euler, Matrix4 } from 'three'
import { Stack, Button, Divider, TextField, InputAdornment, Typography } from '@mui/material'
import { ImmutableVector3, getCenterMultipleReduceMean } from '@modugen/ui-toolkit'
import { NumberInput } from '@ui/forms'

const formatInclinationAngleDegrees = (angle: number) => {
  return numeral(angle * (180 / Math.PI)).format('0.0')
}

const calculateInclination = (shape: { points: ImmutableVector3[] }) => {
  const normal = getPolygonNormal(shape.points)
  const inclination = Math.acos(Math.abs(normal.z))
  return inclination
}

const XYZRotation = (): ReactElement => {
  const { setValue, formState } = useFormContext()

  // Local state for XYZ rotation values (in degrees)
  const [xRotation, setXRotation] = useState<number>(0)
  const [yRotation, setYRotation] = useState<number>(0)
  const [zRotation, setZRotation] = useState<number>(0)
  const formPoints = useWatch({ name: 'points' }) as { x: number; y: number; z: number }[]

  const inclinationAngle =
    formPoints.length >= 3
      ? calculateInclination({
          points: formPoints.map(p => new ImmutableVector3(p.x, p.y, p.z)),
        })
      : 0
  const inclinationAngleFormatted = formatInclinationAngleDegrees(inclinationAngle)

  // Function to reset rotation values
  const resetRotation = useCallback(() => {
    setXRotation(0)
    setYRotation(0)
    setZRotation(0)
  }, [])

  // Function to apply XYZ rotation around center
  const handleClickApply = useCallback(() => {
    // Use current form values if available, otherwise fall back to default values
    if (!formPoints || formPoints.length === 0) return

    // Convert points to ImmutableVector3
    const points = formPoints.map(p => new ImmutableVector3(p.x, p.y, p.z)) as [
      ImmutableVector3,
      ImmutableVector3,
      ImmutableVector3,
      ...ImmutableVector3[],
    ]

    // Calculate center of the slab
    const center = getCenterMultipleReduceMean(points)

    // Convert rotation angles from degrees to radians
    const xRad = (xRotation * Math.PI) / 180
    const yRad = (yRotation * Math.PI) / 180
    const zRad = (zRotation * Math.PI) / 180

    // Create rotation matrix from Euler angles (XYZ order)
    const euler = new Euler(xRad, yRad, zRad, 'XYZ')
    const rotationMatrix = new Matrix4().makeRotationFromEuler(euler)

    // Apply rotation to each point around the center
    const rotatedPoints = points.map(point => {
      // Translate point to origin (relative to center)
      const translatedPoint = point.sub(center)

      // Apply rotation
      const rotatedVector = translatedPoint.v.applyMatrix4(rotationMatrix)

      // Translate back
      const finalPoint = new ImmutableVector3(
        center.x + rotatedVector.x,
        center.y + rotatedVector.y,
        center.z + rotatedVector.z,
      )

      return {
        x: finalPoint.x,
        y: finalPoint.y,
        z: finalPoint.z,
      }
    })

    // Update the form values
    setValue('points', rotatedPoints, { shouldDirty: false })
    resetRotation()
  }, [xRotation, yRotation, zRotation, setValue, formPoints, resetRotation])

  const handleClickReset = useCallback(() => {
    resetRotation()
    // Reset to original points from defaultValues
    if (formState.defaultValues?.points) {
      setValue('points', formState.defaultValues.points, { shouldDirty: false })
    }
  }, [resetRotation, formState.defaultValues, setValue])

  return (
    <Stack direction="column" spacing={1} alignItems={'center'} sx={{ width: '100%' }}>
      <Divider sx={{ width: '100%' }}>Drehung (XYZ)</Divider>

      <Stack
        direction="row"
        spacing={1}
        alignItems={'center'}
        justifyContent={'space-between'}
        sx={{ width: '100%' }}
      >
        <Typography>Neigung {inclinationAngleFormatted}</Typography>
        <TextField
          variant="outlined"
          label="Drehung X"
          size="small"
          placeholder="0,00"
          value={xRotation || ''}
          onChange={event => {
            const value = event.target.value.length ? parseFloat(event.target.value) || 0 : 0
            setXRotation(value)
          }}
          InputProps={{
            endAdornment: <InputAdornment position="end">°</InputAdornment>,
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            inputComponent: NumberInput as any,
          }}
        />

        <TextField
          variant="outlined"
          label="Rot Y"
          size="small"
          placeholder="0,00"
          value={yRotation || ''}
          onChange={event => {
            const value = event.target.value.length ? parseFloat(event.target.value) || 0 : 0
            setYRotation(value)
          }}
          InputProps={{
            endAdornment: <InputAdornment position="end">°</InputAdornment>,
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            inputComponent: NumberInput as any,
          }}
        />
        <TextField
          label="Drehung Z"
          variant="outlined"
          size="small"
          placeholder="0,00"
          value={zRotation || ''}
          onChange={event => {
            const value = event.target.value.length ? parseFloat(event.target.value) || 0 : 0
            setZRotation(value)
          }}
          InputProps={{
            endAdornment: <InputAdornment position="end">°</InputAdornment>,
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            inputComponent: NumberInput as any,
          }}
        />
      </Stack>

      <Stack
        direction="row"
        spacing={1}
        alignItems={'center'}
        justifyContent={'space-between'}
        sx={{ width: '100%' }}
      >
        <Button
          variant="outlined"
          size="small"
          onClick={handleClickApply}
          disabled={xRotation === 0 && yRotation === 0 && zRotation === 0}
          fullWidth
        >
          Anwenden
        </Button>
        <Button size="small" onClick={handleClickReset} fullWidth variant="outlined">
          Zurücksetzen
        </Button>
      </Stack>
    </Stack>
  )
}

export default XYZRotation
